import {
    HeadContent,
    Outlet,
    Scripts,
    createRootRouteWithContext,
} from '@tanstack/react-router';
import appCssUrl from '../styles.css?url';

import { AnalyticsTracker } from '@/components/site/analytics-tracker';
import { ClientRecovery } from '@/components/site/client-recovery';
import { RootErrorPage, AppNotFoundPage } from '@/components/site/error-page';
import { PwaSplash } from '@/components/site/pwa-splash';
import { RegisterServiceWorker } from '@/components/site/register-service-worker';
import { Toaster } from '@/components/ui/sonner';
import type { QueryClient } from '@tanstack/react-query';
import {
    PWA_SPLASH_CRITICAL_CSS,
    pwaDocumentHead,
} from '@/lib/pwa';
import { seoSiteChrome } from '@/lib/seo';
import { appwriteOrigin } from '@/lib/media-url';
import { authMiddleware } from '@/server/functions/auth';
import { loadNavCategories } from '@/server/functions/pages';

/** Matches `--background` so first paint is dark before CSS loads. */
const DOCUMENT_BACKGROUND = '#000000';
const documentHtmlStyle = {
    backgroundColor: DOCUMENT_BACKGROUND,
    colorScheme: 'dark',
} as const;
const documentBodyStyle = { backgroundColor: DOCUMENT_BACKGROUND } as const;

interface RouterContext {
    queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
    staleTime: 5 * 60_000,
    // Root pending would unmount <html>/<body>. Never swap to a blank document.
    pendingMs: Number.POSITIVE_INFINITY,
    loader: async () => {
        const [navData, auth] = await Promise.all([
            loadNavCategories(),
            authMiddleware(),
        ]);
        return {
            ...navData,
            canAccessAdmin: auth.canAccessAdmin,
            isFullAdmin: auth.isFullAdmin,
            staffRole: auth.staffRole,
        };
    },
    errorComponent: RootErrorPage,
    notFoundComponent: AppNotFoundPage,
    head: () => {
        const chrome = seoSiteChrome();
        const pwa = pwaDocumentHead();
        const mediaOrigin = appwriteOrigin();

        return {
            meta: [
                { charSet: 'utf-8' },
                { name: 'viewport', content: 'width=device-width, initial-scale=1' },
                { name: 'theme-color', content: DOCUMENT_BACKGROUND },
                { name: 'color-scheme', content: 'dark' },
                ...pwa.meta,
                ...chrome.meta,
            ],
            styles: [
                {
                    children: `html,body{background-color:${DOCUMENT_BACKGROUND}}${PWA_SPLASH_CRITICAL_CSS}`,
                },
            ],
            links: [
                ...(mediaOrigin
                    ? [{ rel: 'preconnect', href: mediaOrigin, crossOrigin: 'anonymous' as const }]
                    : []),
                {
                    rel: 'preload',
                    href: '/fonts/heebo/heebo-hebrew.woff2',
                    as: 'font',
                    type: 'font/woff2',
                    crossOrigin: 'anonymous',
                },
                {
                    rel: 'preload',
                    href: '/fonts/fbphilosof-regularwebfont.woff',
                    as: 'font',
                    type: 'font/woff',
                    crossOrigin: 'anonymous',
                },
                {
                    rel: 'preload',
                    href: '/fonts/inter-tight/inter-tight-variable-font-wght.ttf',
                    as: 'font',
                    type: 'font/ttf',
                    crossOrigin: 'anonymous',
                },
                {
                    rel: 'preload',
                    href: '/fonts/departure-mono/departure-mono-regular.woff2',
                    as: 'font',
                    type: 'font/woff2',
                    crossOrigin: 'anonymous',
                },
                { rel: 'stylesheet', href: appCssUrl },
                { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
                { rel: 'icon', type: 'image/png', href: '/icon.png' },
                { rel: 'apple-touch-icon', href: '/apple-icon.png' },
                ...pwa.links,
                ...chrome.links,
            ],
            scripts: [...pwa.scripts, ...chrome.scripts],
        };
    },
    component: RootDocument,
});

function RootDocument() {
    return (
        <html
            lang="he"
            dir="rtl"
            className="dark"
            style={documentHtmlStyle}
            suppressHydrationWarning
        >
            <head>
                <HeadContent />
            </head>
            <body style={documentBodyStyle}>
                <PwaSplash />
                <Outlet />
                <ClientRecovery />
                <RegisterServiceWorker />
                <AnalyticsTracker />
                <Toaster />
                <Scripts />
            </body>
        </html>
    );
}
